import { defineStore, storeToRefs } from 'pinia';

import { useMatomo } from '@clovyr/bed/composables/useMatomo';
import { usePrice } from '@clovyr/bed/composables/usePrice';
import type { ComputeProviderConfig, ComputeProviderInfo, Instance } from '@clovyr/pollen';
import {
  ComputeInstanceSize,
  type ComputeProvider,
  type InstanceType,
} from '@clovyr/pollen/compute';
import { getComputeProvider, supportsOAuth } from '@clovyr/pollen/compute/util';
import { getHostByID, wNextHost } from '@clovyr/pollen/fixtures/hosts';
import { getAllQueryParams } from '@clovyr/pollen/http/qs';
import type { Manifest } from '@clovyr/pollen/manifest';
import { AuthState } from '@clovyr/pollen/pollen';
import { type ClovyrAICloudSalesToken, SalesTokenAPI } from '@clovyr/pollen/sales';
import type { SalesPricingContext } from '@clovyr/pollen/subscription';
import { ComputeProviderName } from '@clovyr/pollen/types';
import type { Deployment } from '@clovyr/pollen/types/Deployment';
import { DeploymentAddon } from '@clovyr/pollen/types/Deployment';

import { useBeforeUnload } from '@/composables/useBeforeUnload';
import { useEventBus } from '@/stores/event_bus';
import { ShowAuthForm } from '@/views/Settings/types';

import { type CustomDNSState, useCustomDNS } from '../composables/useCustomDNS';
import { useInstanceLauncher } from '../composables/useInstanceLauncher';
import { usePayments } from '../composables/usePayments';

import {
  advancedSettings,
  type AnswerMap,
  answers,
  basicSettings,
  configErrors,
  deploySettings,
  hasValidSettings,
  initSettings,
  setAnswer,
} from './launch_settings';
import { useLaunchStore } from './launch_store';
import { usePollenStore } from './pollen_store';
import { LauncherState } from './types';
import { persist, rehydrate } from './util';

interface PersistedLauncherState {
  status: LauncherState;
  answers: AnswerMap;
  deployment: Deployment;
  customDNS?: CustomDNSState;
  billingPeriod?: BillingPeriod;
  salesToken?: string;
  salesTier?: string;
  salespersonCode?: string;
}

type BillingPeriod = 'monthly' | 'annual';

const BillingPeriodMonthly: BillingPeriod = 'monthly';
const BillingPeriodAnnual: BillingPeriod = 'annual';

const salesTierLabels: Record<string, string> = {
  tier_1_micro: 'Tier 1: Micro',
  tier_2_small: 'Tier 2: Small',
  tier_3_medium: 'Tier 3: Medium',
  tier_4_enterprise: 'Tier 4: Enterprise',
};

const LAUNCHER_STORE_KEY = 'clovyr/ui/launcher_store';

/**
 * Launcher Store tracks the progress of the launch wizard from init until the actual
 * launch/deployment. The app launch itself is managed in the `Launch Store`.
 */
export const useLauncherStore = defineStore('launcher', () => {
  const router = useRouter();
  const instanceLauncher = useInstanceLauncher();
  const launchStore = useLaunchStore();
  const pollenStore = usePollenStore();
  const customDNS = useCustomDNS();
  const { trackEvent } = useMatomo();
  const payments = usePayments(); // Initialize payments store

  const { getDeploymentApp } = pollenStore;
  const { computeProviderConfigs, authState, isUserAuthenticated } = storeToRefs(pollenStore);
  const { eventBus, once } = useEventBus();

  // primary launch state
  // answers imported from launch_settings
  const status = ref<LauncherState>(LauncherState.ChooseHost); // initialState.status || LauncherState.ChooseHost
  const deployment = ref<Deployment>({} as Deployment);
  // TODO: store this in pollen

  const manifest = ref<Manifest>({} as Manifest); // initialState.app
  const metadata = computed(() => manifest.value?.metadata);

  const selectedHost = ref<ComputeProviderInfo | undefined>();
  const selectedComputeProviderConfig = ref<ComputeProviderConfig>();
  const computeProvider = ref<ComputeProvider | undefined>();
  // Tracks the user's hosting intent even while selectedHost is momentarily cleared
  // (e.g. 'Choose another host' is selected but no specific host is picked yet).
  const hostSelectionIntent = ref<'clovyr' | 'byoh'>('clovyr');

  const instance = ref<Instance>();
  const billingPeriod = ref<BillingPeriod>(BillingPeriodMonthly);
  const salesToken = ref<string>();
  const salesTier = ref<string>();
  const salespersonCode = ref<string>();

  const isClovyrAICloud = computed(() => manifest.value?.metadata?.id === 'clovyr-ai-cloud');
  const showBillingPeriodStep = computed(
    () => isClovyrAICloud.value && selectedHost.value?.id === ComputeProviderName.WNext,
  );
  const billingPeriodLabel = computed(() =>
    billingPeriod.value === BillingPeriodMonthly ? 'Monthly' : 'Annual',
  );
  const salesTierLabel = computed(() =>
    salesTier.value ? salesTierLabels[salesTier.value] || salesTier.value : 'Tier 1: Micro',
  );
  const salesPricingContext = computed<SalesPricingContext | undefined>(() => {
    if (!showBillingPeriodStep.value) {
      return undefined;
    }

    return {
      salesToken: salesToken.value,
      salesTier: salesTier.value,
      billingPeriod: billingPeriod.value,
    };
  });

  function syncComputeSizeToSalesTier() {
    if (!isClovyrAICloud.value || selectedHost.value?.id !== ComputeProviderName.WNext) {
      return;
    }

    if (salesTier.value && salesTier.value !== 'tier_1_micro') {
      deployment.value.size = ComputeInstanceSize.XLarge;
      return;
    }

    deployment.value.size = undefined;
  }

  async function syncSalesTokenState() {
    const query = getAllQueryParams();

    const routeBillingPeriod = query.get('billing_period');
    if (!isClovyrAICloud.value) {
      salesToken.value = undefined;
      salesTier.value = undefined;
      salespersonCode.value = undefined;
      billingPeriod.value = BillingPeriodMonthly;
      return;
    }

    if (routeBillingPeriod === BillingPeriodMonthly || routeBillingPeriod === BillingPeriodAnnual) {
      billingPeriod.value = routeBillingPeriod;
    } else {
      billingPeriod.value = BillingPeriodMonthly;
    }

    const routeSalesToken = query.get('sales_token') || undefined;
    if (!routeSalesToken) {
      salesToken.value = undefined;
      salesTier.value = undefined;
      salespersonCode.value = undefined;
      return;
    }

    if (routeSalesToken === salesToken.value && salesTier.value) {
      return;
    }

    try {
      const resolved: ClovyrAICloudSalesToken =
        await new SalesTokenAPI().getClovyrAICloudSalesToken(routeSalesToken);
      salesToken.value = routeSalesToken;
      salesTier.value = resolved.sales_tier;
      salespersonCode.value = resolved.salesperson_code;
      syncComputeSizeToSalesTier();
    } catch {
      salesToken.value = undefined;
      salesTier.value = undefined;
      salespersonCode.value = undefined;
      syncComputeSizeToSalesTier();
      eventBus.emit('toast:show', {
        message: 'This sales link is invalid or expired.',
        type: 'error',
        duration: 5000,
      });
    }
  }

  function rehydrateState() {
    // first load state from storage
    const initialState: PersistedLauncherState | null = rehydrate(LAUNCHER_STORE_KEY);
    if (initialState && initialState.status) {
      status.value = initialState.status;
      deployment.value = initialState.deployment;
      if (initialState.customDNS) {
        customDNS.state.value = initialState.customDNS;
      }
      billingPeriod.value = initialState.billingPeriod || BillingPeriodMonthly;
      salesToken.value = initialState.salesToken;
      salesTier.value = initialState.salesTier;
      salespersonCode.value = initialState.salespersonCode;

      if (deployment.value.hostingProvider) {
        selectedHost.value = getHostByID(deployment.value.hostingProvider);
        // markRaw: compute providers use private class fields (e.g. #credentials) that
        // break when Vue wraps the instance in a reactive Proxy.
        computeProvider.value = markRaw(getComputeProvider(deployment.value.hostingProvider));
        hostSelectionIntent.value =
          deployment.value.hostingProvider === ComputeProviderName.WNext ? 'clovyr' : 'byoh';
      }

      const savedComputeProviderConfig = computeProviderConfigs.value.find(
        (c) => c.id === deployment.value.computeProviderConfigID,
      );
      if (savedComputeProviderConfig) {
        selectedComputeProviderConfig.value = savedComputeProviderConfig;
        computeProvider.value?.setConfig(savedComputeProviderConfig);
      } else if (deployment.value.hostingProvider === ComputeProviderName.WNext) {
        selectedComputeProviderConfig.value = computeProvider.value?.getConfig();
      }

      const m = getDeploymentApp(deployment.value);
      if (!m) {
        // TODO: throw error?
      } else {
        manifest.value = m;
      }
      initSettings(m, initialState.answers);
      void syncSalesTokenState();

      const query = getAllQueryParams();
      void payments.getSubscription().then(() => {
        // ensure sub is loaded before checking payment
        if (query.get('payment') === 'complete' && payments.hasPaymentOnFile.value) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          void launchApp();
        }
      });
    }
  }
  // must rehydrate before starting watcher, or else persisted state will get
  // overwritten.
  rehydrateState();

  function persistState() {
    const o: PersistedLauncherState = {
      status: status.value,
      answers: answers.value,
      deployment: deployment.value,
      customDNS: customDNS.state.value,
      billingPeriod: billingPeriod.value,
      salesToken: salesToken.value,
      salesTier: salesTier.value,
      salespersonCode: salespersonCode.value,
    };
    persist(LAUNCHER_STORE_KEY, o);
  }

  /**
   * persist state to localstorage on changes
   */
  watch(
    [status, answers, deployment, customDNS.state, billingPeriod, salesToken, salesTier],
    () => {
      if (status.value === LauncherState.Launching) {
        // don't persist when launching. state should be cleared out.
        return;
      }
      persistState();
    },
    {
      deep: true,
    },
  );

  function setState(newState: LauncherState) {
    trackEvent('launcher', 'state', newState);
    status.value = newState;
  }

  /**
   * Reset store back to initial values so that another app can be launched
   */
  function reset() {
    // wipe state so we don't retry on reload of launcher
    persist(LAUNCHER_STORE_KEY, {});
    setState(LauncherState.Settings);
    manifest.value = {} as Manifest;
    deployment.value = {} as Deployment;
    selectedHost.value = undefined;
    computeProvider.value = undefined;
    selectedComputeProviderConfig.value = undefined;
    customDNS.reset();
    billingPeriod.value = BillingPeriodMonthly;
    salesToken.value = undefined;
    salesTier.value = undefined;
    salespersonCode.value = undefined;
    initSettings();
  }

  // ComputeProvider is not a ref to avoid issues with private fields, so we
  // must manually watch selectedComputeProviderConfig for changes.
  // https://github.com/vuejs/core/issues/2981
  const isComputeConfigured = ref(computeProvider.value?.isConfigured() ?? false);
  watch(selectedComputeProviderConfig, () => {
    if (selectedComputeProviderConfig.value) {
      computeProvider.value?.setConfig(selectedComputeProviderConfig.value);
    }
    isComputeConfigured.value = computeProvider.value?.isConfigured() ?? false;
  });

  watch([salesTier, selectedHost, manifest], () => {
    syncComputeSizeToSalesTier();
  });

  // Check if payment should be skipped for third-party hosts without backups
  const isThirdPartyWithoutBackups = computed(
    () =>
      selectedHost.value?.id !== ComputeProviderName.WNext &&
      !deployment.value.addons?.includes(DeploymentAddon.Backups),
  );

  /**
   * is it possible to navigate to next step in launcher?
   */
  function isNextEnabled() {
    switch (status.value) {
      case LauncherState.Settings:
        return hasValidSettings.value && customDNS.isValid.value;
      case LauncherState.ChooseHost:
        return !!selectedHost.value && customDNS.isValid.value;
      case LauncherState.HostCredentials:
        return isComputeConfigured.value;
      case LauncherState.BillingPeriod:
        return !!billingPeriod.value;
      case LauncherState.Review:
        return (
          hasValidSettings.value &&
          !!selectedHost.value &&
          isComputeConfigured.value &&
          customDNS.isValid.value
        );
      case LauncherState.Launching:
        return true;
      default:
        return false;
    }
  }

  async function launchApp() {
    if (
      !(
        isUserAuthenticated.value &&
        authState.value === AuthState.LoggedIn &&
        (payments.hasPaymentOnFile.value || isThirdPartyWithoutBackups.value)
      )
    ) {
      console.warn('User is not authenticated or does not have a payment on file');
      return;
    }

    // Extra check for Clovyr AI Cloud SSO passkey requirement before launching
    const ssoEnabled =
      manifest.value.metadata.id === 'clovyr-ai-cloud' &&
      answers.value['basic.CLOVYR_SSO_ENABLED']?.value === true;
    if (ssoEnabled) {
      try {
        await pollenStore.refreshPasskeys();
      } catch {
        setState(LauncherState.Settings);
        eventBus.emit('toast:show', {
          message: 'Unable to check your passkeys. Please try again.',
          type: 'error',
          duration: 5000,
        });
        return;
      }
      if (!pollenStore.hasPasskey) {
        setState(LauncherState.Settings);
        eventBus.emit('toast:show', {
          message:
            'Add a passkey or turn off Clovyr Local SSO sign-in to set an admin username and password.',
          type: 'error',
          sticky: true,
        });
        return;
      }
    }

    setState(LauncherState.Launching);

    deployment.value.customDomain = customDNS.customDomain.value;
    deployment.value.fqdn = customDNS.state.value.claimedDomain;

    trackEvent('launcher', 'launch', manifest.value.metadata.id);
    if (customDNS.customDomain.value) {
      trackEvent('launcher', 'launchWithCustomDomain');
    }

    void launchStore
      .createLaunch(
        manifest.value,
        deployment.value,
        selectedHost.value!,
        selectedComputeProviderConfig.value!,
        answers.value,
        salesPricingContext.value,
      )
      .then(() => {
        // Start launching immediately in the background while navigating to the detail page
        const { id } = deployment.value;
        void launchStore.startLaunch(id).finally(() => {
          useBeforeUnload().removeBlocker(`deployment/${id}`);
        });
        const params = pollenStore.getLibraryRouteParams(deployment.value);
        void router
          .push({
            name: 'libraryItemPrefix' in params ? 'LibraryAppDetailQualified' : 'LibraryAppDetail',
            params,
          })
          .then(() => {
            reset();
          });
      })
      .catch((error) => {
        console.error('Failed to launch app:', error);
        // Optionally, show an error message to the user
        eventBus.emit('toast:show', {
          message: 'Failed to launch the app. Please try again.',
          type: 'error',
          duration: 5000,
        });
      });
  }

  /**
   * Handle the launch process, including authentication and payment.
   *
   * Called when the user clicks the "Launch" button in the launch wizard, from the Review screen.
   *
   * If the user is not logged in, show the login modal and wait for authentication.
   * If the user is authenticated and has a payment on file, proceed with launching the app.
   */
  function handleLaunch() {
    // If the user is not logged in, show the login modal
    if (authState.value !== AuthState.LoggedIn) {
      eventBus.emit('modal:login:show', {
        form: ShowAuthForm.Signup,
        opts: { showAppLaunchMarketing: true, app: manifest.value },
      });

      once('modal:login:close', async () => {
        if (isUserAuthenticated.value && authState.value === AuthState.LoggedIn) {
          // console.log('User authenticated successfully');
          await payments.captureLaunchPayment(
            manifest.value.metadata.id,
            salesPricingContext.value,
          );

          // continue if payment previously captured (user signed in vs new user sign up which
          // requires payment)
          void launchApp();
        } else {
          console.error('Authentication failed');
        }
      });

      return; // Exit early to wait for authentication
    }

    // **NEW: Only show payment modal if payment is NOT on file**
    // Skip payment for third-party hosts when backups are NOT selected

    if (payments.hasPaymentOnFile.value || isThirdPartyWithoutBackups.value) {
      void launchApp();
      return;
    }

    // always show payment/confirmation modal
    once('launch:continue', launchApp);
    once('modal:launch_payment:close', () => {
      // cleanup
      eventBus.off('launch:continue', launchApp);
    });
    void eventBus.emit('modal:launch_payment:show', { app: manifest.value });

    // If already logged in, ensure payment on file
    // await payments.captureLaunchPayment(manifest.value.metadata.id);

    // // If already logged in and payment on file, proceed with launching the app
    // launchApp();
  }

  /**
   * try to navigate to the next step in the launcher
   */
  async function next() {
    if (!isNextEnabled()) {
      return;
    }

    switch (status.value) {
      case LauncherState.Settings:
        if (
          selectedHost.value?.id === ComputeProviderName.WNext ||
          selectedComputeProviderConfig.value !== undefined
        ) {
          setState(
            showBillingPeriodStep.value ? LauncherState.BillingPeriod : LauncherState.Review,
          );
        } else {
          setState(LauncherState.ChooseHost);
        }
        break;

      case LauncherState.ChooseHost:
        if (selectedHost.value?.id === ComputeProviderName.WNext) {
          setState(
            showBillingPeriodStep.value ? LauncherState.BillingPeriod : LauncherState.Review,
          );
        } else {
          setState(LauncherState.HostCredentials);
        }
        break;

      case LauncherState.HostCredentials:
        if (selectedHost.value) {
          trackEvent('launcher', 'savedHostCredentials', selectedHost.value.id);
        }
        setState(showBillingPeriodStep.value ? LauncherState.BillingPeriod : LauncherState.Review);
        break;

      case LauncherState.BillingPeriod:
        setState(LauncherState.Review);
        break;

      case LauncherState.Review:
        handleLaunch();
        break;

      default:
        break;
    }
  }

  /**
   * is it possible to nav to previous step?
   */
  function isBackEnabled() {
    switch (status.value) {
      case LauncherState.Settings:
        return true;
      case LauncherState.ChooseHost:
        return true;
      case LauncherState.HostCredentials:
        return true;
      case LauncherState.BillingPeriod:
        return true;
      case LauncherState.Review:
        return true;
      case LauncherState.Launching:
        return false;
      default:
        return false;
    }
  }

  /**
   * try to navigate to the previous step in the launcher
   */
  function back(newState?: LauncherState) {
    if (!isBackEnabled()) {
      return;
    }
    switch (status.value) {
      case LauncherState.Settings:
        setState(LauncherState.ChooseHost);
        break;
      case LauncherState.ChooseHost:
        setState(LauncherState.Settings);
        break;
      case LauncherState.HostCredentials:
        setState(LauncherState.ChooseHost);
        break;
      case LauncherState.BillingPeriod:
        if (selectedHost.value?.id === ComputeProviderName.WNext) {
          setState(LauncherState.Settings);
        } else {
          setState(LauncherState.HostCredentials);
        }
        break;
      case LauncherState.Review:
        if (showBillingPeriodStep.value) {
          setState(LauncherState.BillingPeriod);
        } else if (
          newState &&
          (newState === LauncherState.ChooseHost || newState === LauncherState.HostCredentials)
        ) {
          // allow passing a state directly
          setState(newState);
        } else if (selectedHost.value?.id === ComputeProviderName.WNext) {
          setState(LauncherState.ChooseHost);
        } else {
          setState(LauncherState.HostCredentials);
        }
        break;
      default:
    }
  }

  /**
   * Initialize or reset the launcher to the initial state for a new app.
   *
   * Only a single app can be worked on at a given time.
   *
   * Called from AppLauncher either when clicking 'Launch' or, possibly, when
   * reloading the page.
   *
   * @param app
   */
  /* eslint-disable @typescript-eslint/no-use-before-define */
  function initialize(app?: Manifest) {
    // compare already loaded state (if any) with requested app (obtained via route prop)
    // manifest can be set either during rehydration or below
    if (app && manifest.value && app.metadata.id !== manifest.value?.metadata?.id) {
      setState(LauncherState.Settings);
      manifest.value = app;
      deployment.value = pollenStore.createDeployment(app.metadata, wNextHost);

      // if we have any stored configs, use the first one, otherwise use wnext
      if (computeProviderConfigs.value.length > 0) {
        const firstConfig = computeProviderConfigs.value[0];
        setSelectedHost(getHostByID(firstConfig.providerID));
        void setComputeProviderConfig(firstConfig);
      } else {
        setSelectedHost(wNextHost);
      }

      initSettings(app, {});
      if (app.metadata.id === 'clovyr-ai-cloud') {
        const ssoAnswer = answers.value['basic.CLOVYR_SSO_ENABLED'];
        if (!pollenStore.isUserAuthenticated && !ssoAnswer?.isTouched) {
          setAnswer('basic.CLOVYR_SSO_ENABLED', true);
        } else {
          void pollenStore
            .refreshPasskeys()
            .then(() => {
              if (pollenStore.hasPasskey && !ssoAnswer?.isTouched) {
                setAnswer('basic.CLOVYR_SSO_ENABLED', true);
              }
            })
            .catch(() => undefined);
        }
      }
      void syncSalesTokenState();
      persistState();
    }
    // throw err if no app given? is there a scenario where this is/should be possible?
    // applauncher should probably be checking its props before getting to this point.
  }
  /* eslint-enable @typescript-eslint/no-use-before-define */

  /**
   * Change selected compute provider but zero out config
   *
   * @param host
   */
  function setSelectedHost(host?: ComputeProviderInfo) {
    if (selectedHost.value === host) {
      return;
    }
    if (!host) {
      // null all values - i.e., when 'choose another host' is selected
      selectedHost.value = undefined;
      computeProvider.value = undefined;
      deployment.value.hostingProvider = ComputeProviderName.WNext; // back to default for now (because not-null)
      deployment.value.computeProviderConfigID = undefined;
      selectedComputeProviderConfig.value = undefined;
      hostSelectionIntent.value = 'byoh';
      return;
    }

    selectedHost.value = host;
    computeProvider.value = markRaw(getComputeProvider(host.id));
    deployment.value.hostingProvider = host.id;
    deployment.value.computeProviderConfigID = undefined;
    deployment.value.region = host.defaultRegion;
    hostSelectionIntent.value = host.id === ComputeProviderName.WNext ? 'clovyr' : 'byoh';

    if (host.id === ComputeProviderName.WNext) {
      selectedComputeProviderConfig.value = computeProvider.value.getConfig();
    } else {
      selectedComputeProviderConfig.value = undefined;
    }
  }

  function toggleAddon(addon: DeploymentAddon, selected: boolean) {
    if (selected) {
      if (!deployment.value.addons?.includes(addon)) {
        deployment.value.addons ||= [];
        deployment.value.addons.push(addon);
      }
    } else if (deployment.value.addons) {
      deployment.value.addons = deployment.value.addons.filter((d) => d !== addon);
    }
  }

  /**
   * Get the instance type tuple for the given host
   *
   * @param host
   * @returns
   */
  function getProviderInstanceType(host: ComputeProviderInfo): InstanceType {
    const provider = getComputeProvider(host);
    return provider.getInstanceType(
      manifest.value.deployment?.compute?.size || ComputeInstanceSize.Small,
    );
  }

  const computeSize = computed(
    () =>
      deployment.value.size ||
      manifest.value.deployment?.compute?.size ||
      ComputeInstanceSize.Small,
  );
  const { wnextAppPrice } = usePrice(manifest, computeSize, salesTier, billingPeriod);

  function setBillingPeriod(period: BillingPeriod) {
    billingPeriod.value = period;
  }

  /**
   * kick off oauth flow for current compute provider to get auth token
   */
  async function doOauth(): Promise<{ token: string }> {
    if (!selectedHost.value || !computeProvider.value) {
      return Promise.reject(new Error('No host selected'));
    }

    const token = await instanceLauncher.doOauth(selectedHost.value.id);
    if (token) {
      if (computeProvider.value && supportsOAuth(computeProvider.value) && !token) {
        return Promise.reject(new Error('No auth token'));
      }
    }
    return { token };
  }

  /**
   * Used to pass back compute provider configs, such as oauth or other access
   * tokens.
   *
   * @param config
   */
  async function setComputeProviderConfig(config: ComputeProviderConfig) {
    if (selectedHost.value && computeProvider.value) {
      selectedComputeProviderConfig.value = config;
      computeProvider.value.setConfig(config);

      if (computeProvider.value.isConfigured()) {
        deployment.value.computeProviderConfigID = config.id;
        persistState();
      }
    }
  }

  return {
    status,
    deployment,
    computeProviderConfigs,
    selectedComputeProviderConfig,
    computeProvider,
    manifest,
    metadata,
    instance,
    isClovyrAICloud,

    reset,
    initialize,
    back,
    next,
    isNextEnabled: computed(isNextEnabled),
    isBackEnabled: computed(isBackEnabled),

    answers,
    setAnswer,
    basicSettings,
    advancedSettings,
    deploySettings,
    hasValidSettings,
    configErrors,

    selectedHost,
    hostSelectionIntent,
    getProviderInstanceType,
    wnextAppPrice,
    billingPeriod,
    billingPeriodLabel,
    salesTier,
    salesTierLabel,
    salespersonCode,
    salesToken,
    showBillingPeriodStep,
    setBillingPeriod,
    setSelectedHost,
    toggleAddon,

    doOauth,
    setComputeProviderConfig,
    customDNS,
  };
});
